import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ChevronLeft, Clock, Heart, UserPlus, Lock } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import GuestHeader from "@/components/GuestHeader";
import BlobImage from "@/components/BlobImage";
import GuestRatingPrompt from "@/components/GuestRatingPrompt";
import { getGuest } from "@/lib/guest";
import { useGuestSessionWatch } from "@/lib/useGuestSessionWatch";
import { useIpadLive } from "@/lib/useIpadLive";
import { useTrackView, trackAction } from "@/lib/analyticsTracker";
import { useAppTz, dayKeyInTz, fmtTime } from "@/lib/tz";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function GuestBooking() {
  useGuestSessionWatch();
  useTrackView("book");
  const { treatmentId } = useParams();
  const navigate = useNavigate();
  const appTz = useAppTz();
  const { name: guestName, id: guestId, roster, charter_arrival: storedArrival, charter_departure: storedDeparture } = getGuest();
  const me = (roster || []).find((r) => r.guest_id === guestId);
  const isFemale = !!me?.female;
  // Guest's persistent email lives on the roster now (see /public/guest-profile
  // + the GDPR consent card on /cabin). If they saved one, we prefill it here
  // so booking is a single tap — they can still overwrite it per-booking.
  const savedEmail = me?.email || "";

  const [treatment, setTreatment] = useState(null);
  const [settings, setSettings] = useState(null);
  const [crew, setCrew] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState("");
  const [date, setDate] = useState(new Date());
  // Live charter window — always fetched on mount so stale localStorage sessions
  // from before this feature shipped still get properly constrained.
  const [charterArrival, setCharterArrival] = useState(storedArrival || null);
  const [charterDeparture, setCharterDeparture] = useState(storedDeparture || null);
  const [busy, setBusy] = useState([]);
  const [slot, setSlot] = useState("");
  const [email, setEmail] = useState(savedEmail);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [chosenDuration, setChosenDuration] = useState(null);
  const [answers, setAnswers] = useState({});
  const [partnerGuestId, setPartnerGuestId] = useState("");
  const [asCouple, setAsCouple] = useState(false);
  // "none" | "cabin" (same-cabin partner) | "friend" (from another cabin)
  const [dualMode, setDualMode] = useState("none");
  const [friends, setFriends] = useState([]);
  // Optional back-to-back follow-up treatment — same crew, no prep gap.
  // Stores the FULL treatment record so we can show name / duration /
  // price + compute the combined slot duration for the picker.
  const [allTreatments, setAllTreatments] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [followupId, setFollowupId] = useState("");
  // Category-first browsing: guest picks a category tile → treatment
  // thumbnails within that category unfurl. `""` = no category picked
  // yet (initial state). Also cleared when the follow-up is removed.
  const [followupCategory, setFollowupCategory] = useState("");
  const followup = useMemo(
    () => (followupId ? allTreatments.find((t) => t.id === followupId) : null),
    [followupId, allTreatments],
  );
  const followupDuration = followup ? Number(followup.duration_min) || 60 : 0;
  const followupPrice = followup ? Number(followup.price) || 0 : 0;

  // Adults-only gate. Reads the guest's age off the cabin roster (crew-entered)
  // and compares against the current treatment's `adults_only` flag (or the
  // parent category's flag). Under-18 → non-bookable with a polite banner.
  // Crew can also flag `minor === true` for guests with unknown/wrong ages;
  // that also triggers the gate. Matches backend `_validate_booking_rules`.
  const guestIsUnderAge = useMemo(() => {
    if (!me) return false;
    if (me.minor === true) return true;
    const raw = me.age;
    if (raw === null || raw === undefined || raw === "") return false;
    const n = Number(raw);
    return Number.isFinite(n) && n < 18;
  }, [me]);
  const isAdultBlocked = useMemo(() => {
    if (!treatment) return false;
    if (!guestIsUnderAge) return false;
    if (treatment.adults_only) return true;
    if (treatment.category_id) {
      const cat = (allCategories || []).find((c) => c.id === treatment.category_id);
      if (cat && cat.adults_only) return true;
    }
    return false;
  }, [treatment, allCategories, guestIsUnderAge]);
  const adultsOnlyMsg = (settings?.adults_only_message || "").trim()
    || "Unfortunately this treatment is not proper for below 18 years old.";

  useEffect(() => {
    if (!guestName) { navigate("/"); return; }
    // Always refresh the charter window so pre-existing sessions get constrained too.
    if (guestId) {
      api.get("/public/my-charter", { params: { guest_id: guestId } })
        .then(({ data }) => {
          if (data.arrival) { setCharterArrival(data.arrival); localStorage.setItem("yacht_charter_arrival", data.arrival); }
          if (data.departure) { setCharterDeparture(data.departure); localStorage.setItem("yacht_charter_departure", data.departure); }
        })
        .catch(() => {});
    }
    (async () => {
      try {
        const [tr, s, cr, cats] = await Promise.all([
          api.get("/public/treatments"),
          api.get("/public/settings"),
          api.get("/public/crew"),
          api.get("/public/treatment-categories"),
        ]);
        const t = (tr.data || []).find((x) => x.id === treatmentId);
        if (!t) { navigate("/menu"); return; }
        setTreatment(t);
        // Keep the full list for the "add follow-up" picker below.
        setAllTreatments((tr.data || []).filter((x) => x.active !== false));
        setAllCategories(cats.data || []);
        setSettings(s.data);
        setCrew((cr.data || []).filter((c) => c.active !== false));
        setChosenDuration(t.duration_min);
        // Pre-fetch guests from other cabins for the "Invite a friend" picker,
        // only meaningful for treatments that allow duals.
        if (t.couples_allowed && guestId) {
          try {
            const { data: fr } = await api.get("/public/other-cabin-guests", { params: { guest_id: guestId } });
            setFriends(fr || []);
          } catch { setFriends([]); }
        }
      } catch {
        toast.error("Could not load treatment");
        navigate("/menu");
      }
    })();
    // eslint-disable-next-line
  }, [treatmentId]);

  // Live re-sync: when a manager edits treatments / crew / categories /
  // settings, refresh the booking page silently so a change made mid-flow
  // (e.g. a treatment marked adults_only, or the rejection message reworded)
  // takes effect without the guest relaunching the app.
  useIpadLive((ev) => {
    if (!ev?.type || !treatmentId) return;
    const isContent = ev.type === "content.updated" && ["treatments", "crew", "treatment_categories", "settings"].includes(ev.kind);
    if (ev.type !== "settings.updated" && !isContent) return;
    Promise.all([
      api.get("/public/treatments"),
      api.get("/public/settings"),
      api.get("/public/crew"),
      api.get("/public/treatment-categories"),
    ]).then(([tr, s, cr, cats]) => {
      const t = (tr.data || []).find((x) => x.id === treatmentId);
      if (t) setTreatment(t);
      setAllTreatments((tr.data || []).filter((x) => x.active !== false));
      setAllCategories(cats.data || []);
      setSettings(s.data || {});
      setCrew((cr.data || []).filter((c) => c.active !== false));
    }).catch(() => { /* best-effort */ });
  });

  const dayKey = useMemo(() => {
    // Anchored to the yacht's app timezone (settings.timezone) so the
    // /public/availability day-key aligns with how the Spa iPad's calendar
    // buckets the same bookings — matches the tz fix landed for SpaOpsView
    // (see tz.js dayKeyInTz). Previously used `getFullYear/getMonth/getDate`
    // which returned the browser's local day and could disagree with the
    // spa's declared operating timezone whenever a guest booked from a
    // laptop in a different zone.
    const d = date || new Date();
    return dayKeyInTz(d.toISOString(), appTz);
  }, [date, appTz]);

  useEffect(() => {
    if (!treatment) return;
    (async () => {
      try {
        const { data } = await api.get("/public/availability", { params: { treatment_id: treatment.id, day: dayKey } });
        setBusy(data || []);
      } catch { setBusy([]); }
    })();
  }, [dayKey, treatment]);

  const durationOptions = useMemo(() => {
    if (!treatment) return [];
    const base = [{ minutes: treatment.duration_min, price: treatment.price, label: "" }];
    const extra = (treatment.durations || []).filter((d) => Number(d.minutes) !== treatment.duration_min);
    return [...base, ...extra].map((d) => ({ minutes: Number(d.minutes), price: Number(d.price) || 0, label: d.label || "" }));
  }, [treatment]);

  const currentPrice = useMemo(() => {
    const opt = durationOptions.find((d) => d.minutes === chosenDuration);
    return opt?.price ?? treatment?.price ?? 0;
  }, [durationOptions, chosenDuration, treatment]);

  // Filter crew by treatment's allowed crew_ids (empty = all)
  const providingCrew = useMemo(() => {
    if (!treatment) return crew;
    const allowed = treatment.crew_ids || [];
    if (!allowed.length) return crew;
    return crew.filter((c) => allowed.includes(c.id));
  }, [crew, treatment]);

  const slots = useMemo(() => {
    try {
      if (!settings || !treatment || !(date instanceof Date) || isNaN(date.getTime())) return [];
      const [oh, om] = (settings.open_time || "09:00").split(":").map(Number);
      const [ch, cm] = (settings.close_time || "20:00").split(":").map(Number);
      if (![oh, om, ch, cm].every((n) => Number.isFinite(n))) return [];
      const dur = (Number(chosenDuration) || Number(treatment.duration_min) || 60) + (followup ? followupDuration : 0);
      const prep = Number(settings.prep_time_min) || 0;
      const minNoticeMin = Number(
        treatment.min_notice_min ?? settings.global_min_notice_min ?? 120
      ) || 0;
      const cutoff = new Date(Date.now() + minNoticeMin * 60 * 1000);
      const out = [];
      const base = new Date(date.getTime()); base.setHours(oh, om, 0, 0);
      const close = new Date(date.getTime()); close.setHours(ch, cm, 0, 0);
      const stepMin = 5;
      let iter = 0;
      while (base.getTime() + dur * 60000 <= close.getTime() && iter < 500) {
        const s = new Date(base.getTime());
        const e = new Date(base.getTime() + dur * 60000);
        const bufMs = prep * 60000;
        const overlap = (busy || []).some((b) => {
          const bs = new Date(b.start_at).getTime() - bufMs;
          const be = new Date(b.end_at).getTime() + bufMs;
          return s.getTime() < be && e.getTime() > bs;
        });
        const tooSoon = s < cutoff;
        // Render slot labels in the yacht's timezone so a guest booking
        // from a laptop in a foreign zone sees the same "14:00" the Spa
        // iPad will render on the calendar. Prevents the "picked 14:00,
        // shows up as 12:00 on Spa Ops" class of confusion.
        out.push({ iso: s.toISOString(), label: fmtTime(s.toISOString(), appTz), disabled: overlap || tooSoon });
        base.setMinutes(base.getMinutes() + stepMin); iter++;
      }
      return out;
    } catch { return []; }
  }, [settings, treatment, date, busy, chosenDuration, appTz, followup, followupDuration]);

  // Two-step picker: group slots by hour so guests pick H then M (5-min).
  const timeGrid = useMemo(() => {
    const byHour = {};
    for (const s of slots) {
      const [hh, mm] = s.label.split(":");
      (byHour[hh] = byHour[hh] || []).push({ ...s, mm });
    }
    const hours = Object.keys(byHour).sort();
    return { hours, byHour };
  }, [slots]);

  const selectedHour = useMemo(() => {
    if (!slot) return "";
    const found = slots.find((s) => s.iso === slot);
    return found ? found.label.slice(0, 2) : "";
  }, [slot, slots]);

  const [pickedHour, setPickedHour] = useState("");
  useEffect(() => {
    // clear stale slot/hour when day/duration changes and slot no longer exists
    if (slot && !slots.some((s) => s.iso === slot)) setSlot("");
    if (pickedHour && !timeGrid.hours.includes(pickedHour)) setPickedHour("");
    if (!pickedHour && selectedHour) setPickedHour(selectedHour);
  }, [slots, timeGrid, selectedHour]); // eslint-disable-line

  const couplesPartner = useMemo(() => {
    if (!Array.isArray(roster)) return null;
    // Prefer explicit partner set on my guest record; also allow selecting anyone else in roster
    const meRec = roster.find((r) => r.guest_id === guestId);
    if (meRec?.partner_guest_id) return roster.find((r) => r.guest_id === meRec.partner_guest_id) || null;
    return null;
  }, [roster, guestId]);

  const submit = async () => {
    if (!slot) { toast.error("Please pick a time"); return; }
    setSubmitting(true);
    try {
      const activeQuestions = [
        ...(treatment.questions || []),
        ...(isFemale ? (treatment.questions_female || []) : []),
      ];
      const answers_payload = activeQuestions.map((q) => ({
        question_id: q.id,
        question_text: q.text,
        answer: answers[q.id] ?? "",
      })).filter((a) => a.answer !== "");
      const { data } = await api.post("/public/bookings", {
        guest_name: guestName,
        // Email is centralised on the guest's cabin-iPad GDPR panel;
        // backend falls back to the persistent profile email when this
        // field is null. See /public/guest-profile.
        guest_email: null,
        guest_id: guestId || null,
        treatment_id: treatment.id,
        crew_id: selectedCrew || null,
        start_at: slot,
        notes,
        duration_min: chosenDuration || treatment.duration_min,
        answers: answers_payload,
        partner_guest_id: asCouple && partnerGuestId ? partnerGuestId : (asCouple && couplesPartner ? couplesPartner.guest_id : null),
        followup_treatment_id: followupId || null,
      });
      trackAction("book", "booking_confirmed", { treatment_id: treatment.id, has_followup: !!followupId, couple: !!asCouple });
      navigate("/confirmation", { state: { booking: data, treatment } });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!treatment || !settings) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  const showPrice = !treatment.price_hidden;
  const availablePartners = (roster || []).filter((r) => r.guest_id !== guestId);

  return (
    <div className="min-h-screen bg-background">
      <GuestHeader guestName={guestName} />
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8">
        <button onClick={() => navigate("/menu")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-menu">
          <ChevronLeft className="h-4 w-4" /> Back to menu
        </button>
        <div className="mt-6">
          <GuestRatingPrompt guestId={guestId} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 space-y-10">
        {/* Header — full width. Title first (per user's guide) so
            the guest lands on the treatment name, not the photo, and
            the same running order works on portrait AND landscape. */}
        <header data-testid="booking-header">
          <div className="overline text-accent mb-3">Reserving</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-4">{treatment.name}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-muted-foreground mb-3">
            <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{chosenDuration} min</span>
            {showPrice && <span className="font-display text-2xl text-accent">€{Number(currentPrice).toFixed(0)}</span>}
            {treatment.category && <span className="overline text-primary/50">· {treatment.category}</span>}
          </div>
          <p className="text-foreground/80 leading-relaxed max-w-3xl">{treatment.description}</p>
        </header>

        {isAdultBlocked && (
          <div
            className="border border-primary/40 bg-primary/5 text-foreground px-5 py-4 flex items-start gap-3 max-w-3xl"
            data-testid="adults-only-banner"
          >
            <Lock className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <div className="overline text-primary text-xs mb-1">Adults only (18+)</div>
              <div className="text-sm leading-relaxed">
                {adultsOnlyMsg}
              </div>
            </div>
          </div>
        )}

        {/* Photo + Calendar — always side-by-side (portrait AND
            landscape) so the guest never scrolls a full screen of
            photo before seeing the date grid. Calendar drives the
            row height (natural shadcn size) — `items-stretch` (grid
            default) makes the photo match, and a `max-w-3xl` cap
            keeps the pair proportional to the header column above. */}
        <div className="grid grid-cols-2 gap-4 items-stretch max-w-3xl" data-testid="booking-visual-row">
          <div className="overflow-hidden bg-muted" data-testid="booking-hero-image">
            <BlobImage path={treatment.image_path || null} src={FALLBACK_IMG} alt={treatment.name}
              className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} />
          </div>
          <div className="border border-border bg-card p-3 flex flex-col" data-testid="booking-calendar-card">
            <div className="overline text-primary/60 mb-2 text-center">Choose a date</div>
            {/* Centre the shadcn Calendar horizontally so left/right
                margins are visually identical inside the card — the
                natural DayPicker layout is left-aligned and used to
                leave a noticeable gutter on the right when the card
                stretched wider than the grid to match the photo's
                height. */}
            <div className="flex justify-center">
              <Calendar
                mode="single" selected={date}
                onSelect={(d) => { try { if (d instanceof Date && !isNaN(d.getTime())) setDate(d); } catch {} }}
                disabled={(day) => {
                  const today = new Date(); today.setHours(0,0,0,0);
                  if (day < today) return true;
                  if (charterArrival) {
                    const a = new Date(charterArrival); a.setHours(0,0,0,0);
                    if (day < a) return true;
                  }
                  if (charterDeparture) {
                    const dep = new Date(charterDeparture); dep.setHours(23,59,59,999);
                    if (day > dep) return true;
                  }
                  return false;
                }}
                data-testid="booking-calendar"
              />
            </div>
            {(charterArrival || charterDeparture) && (
              <p className="text-[11px] text-muted-foreground mt-2 text-center" data-testid="charter-window-note">
                Bookings are open only within your charter
                {charterArrival ? `: ${new Date(charterArrival).toLocaleDateString()}` : ""}
                {charterDeparture ? ` — ${new Date(charterDeparture).toLocaleDateString()}` : ""}.
              </p>
            )}
          </div>
        </div>

        {/* Everything below — full width single column. */}
        <div className="space-y-10">
          {/* Step 1: Duration picker if multiple */}
          {durationOptions.length > 1 && (
            <div>
              <div className="overline text-primary/60 mb-3">Choose duration</div>
              <div className="flex flex-wrap gap-3" data-testid="duration-options">
                {durationOptions.map((d) => {
                  const active = chosenDuration === d.minutes;
                  return (
                    <button
                      key={d.minutes}
                      onClick={() => setChosenDuration(d.minutes)}
                      className={`px-5 h-14 border-2 rounded-full text-sm transition-all flex items-center gap-3 ${active ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}
                      data-testid={`duration-${d.minutes}`}
                    >
                      <span className="font-medium">{d.minutes} min</span>
                      {d.label && <span className="opacity-80 text-xs">· {d.label}</span>}
                      {showPrice && <span className="font-display text-lg">€{d.price}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Dual treatment picker — same-cabin partner OR invite a friend */}
          {treatment.couples_allowed && (availablePartners.length > 0 || friends.length > 0) && (
            <div>
              <div className="overline text-primary/60 mb-3 flex items-center gap-2"><Heart className="h-3 w-3" /> Book as a dual?</div>
              <div className="flex flex-wrap gap-3" data-testid="dual-mode-toggle">
                <button
                  onClick={() => { setDualMode("none"); setAsCouple(false); setPartnerGuestId(""); }}
                  className={`px-5 h-12 border-2 rounded-full text-sm transition-all ${dualMode === "none" ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}
                  data-testid="dual-none"
                >Just me</button>
                {availablePartners.length > 0 && (
                  <button
                    onClick={() => { setDualMode("cabin"); setAsCouple(true); if (!partnerGuestId && couplesPartner) setPartnerGuestId(couplesPartner.guest_id); }}
                    className={`px-5 h-12 border-2 rounded-full text-sm transition-all inline-flex items-center gap-2 ${dualMode === "cabin" ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}
                    data-testid="dual-cabin"
                  ><Heart className="h-3.5 w-3.5" /> With cabin partner</button>
                )}
                {friends.length > 0 && (
                  <button
                    onClick={() => { setDualMode("friend"); setAsCouple(true); setPartnerGuestId(""); }}
                    className={`px-5 h-12 border-2 rounded-full text-sm transition-all inline-flex items-center gap-2 ${dualMode === "friend" ? "border-emerald-600 bg-emerald-600 text-white" : "border-border hover:border-emerald-600"}`}
                    data-testid="dual-friend"
                  ><UserPlus className="h-3.5 w-3.5" /> Invite a friend</button>
                )}
              </div>

              {dualMode === "cabin" && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="partner-options">
                  {availablePartners.map((p) => (
                    <button
                      key={p.guest_id}
                      onClick={() => setPartnerGuestId(p.guest_id)}
                      className={`px-4 h-10 border rounded-full text-sm transition-colors ${partnerGuestId === p.guest_id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                    >{p.guest_name}</button>
                  ))}
                </div>
              )}

              {dualMode === "friend" && (
                <div className="mt-3" data-testid="friend-options">
                  <p className="text-xs text-muted-foreground mb-2">
                    Pick a guest from another cabin — they'll be booked into the same slot.
                    Please confirm with them first; we can't hide names for privacy in the dual booking.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {friends.map((f) => (
                      <button
                        key={f.guest_id}
                        onClick={() => setPartnerGuestId(f.guest_id)}
                        data-testid={`friend-${f.guest_id}`}
                        className={`px-4 h-11 border rounded-full text-sm transition-colors inline-flex items-center gap-2 ${partnerGuestId === f.guest_id ? "bg-emerald-600 text-white border-emerald-600" : "border-border hover:border-emerald-600"}`}
                      >
                        <UserPlus className="h-3 w-3" />
                        {f.guest_name}
                        <span className="text-[10px] opacity-70">· {f.cabin_name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
{/* --- LEGACY COUPLES BLOCK BELOW is replaced by the dual picker above --- */}
          {false && treatment.couples_allowed && availablePartners.length > 0 && (
            <div>
              <div className="overline text-primary/60 mb-3 flex items-center gap-2"><Heart className="h-3 w-3" /> Book as couple?</div>
              <div className="flex flex-wrap gap-3" data-testid="couples-toggle">
                <button
                  onClick={() => setAsCouple(false)}
                  className={`px-5 h-12 border-2 rounded-full text-sm transition-all ${!asCouple ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}
                  data-testid="couples-no"
                >Just me</button>
                <button
                  onClick={() => { setAsCouple(true); if (!partnerGuestId && couplesPartner) setPartnerGuestId(couplesPartner.guest_id); }}
                  className={`px-5 h-12 border-2 rounded-full text-sm transition-all ${asCouple ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}
                  data-testid="couples-yes"
                >With partner</button>
              </div>
              {asCouple && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="partner-options">
                  {availablePartners.map((p) => (
                    <button
                      key={p.guest_id}
                      onClick={() => setPartnerGuestId(p.guest_id)}
                      className={`px-4 h-10 border rounded-full text-sm transition-colors ${partnerGuestId === p.guest_id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                      data-testid={`partner-${p.guest_id}`}
                    >{p.guest_name}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <div className="overline text-primary/60 mb-3">Pick a time</div>
            {slots.length === 0 ? (
              <div className="text-sm text-muted-foreground">No slots available.</div>
            ) : (
              <div className="space-y-5" data-testid="time-slots">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Hour</div>
                  <div className="flex flex-wrap gap-2" data-testid="time-hour-row">
                    {timeGrid.hours.map((h) => {
                      const disabled = (timeGrid.byHour[h] || []).every((s) => s.disabled);
                      const active = pickedHour === h;
                      return (
                        <button
                          key={h}
                          disabled={disabled}
                          onClick={() => { setPickedHour(h); if (selectedHour !== h) setSlot(""); }}
                          className={`min-w-[3.25rem] h-11 px-3 border text-sm font-mono transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : disabled ? "border-border text-muted-foreground/40 line-through cursor-not-allowed" : "border-border hover:border-primary"}`}
                          data-testid={`hour-${h}`}
                        >{h}</button>
                      );
                    })}
                  </div>
                </div>
                {pickedHour && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Minutes (5-min steps)</div>
                    <div className="grid grid-cols-6 sm:grid-cols-12 gap-2" data-testid="time-minute-row">
                      {(timeGrid.byHour[pickedHour] || []).map((s) => (
                        <button
                          key={s.iso}
                          disabled={s.disabled}
                          onClick={() => setSlot(s.iso)}
                          className={`h-11 border text-sm font-mono transition-colors ${slot === s.iso ? "bg-primary text-primary-foreground border-primary" : s.disabled ? "border-border text-muted-foreground/40 line-through cursor-not-allowed" : "border-border hover:border-primary"}`}
                          data-testid={`slot-${s.label}`}
                          aria-label={`${pickedHour}:${s.mm}`}
                        >:{s.mm}</button>
                      ))}
                    </div>
                  </div>
                )}
                {slot && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Selected:</span>{" "}
                    <span className="font-mono text-primary text-base" data-testid="selected-time">
                      {slots.find((s) => s.iso === slot)?.label}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {providingCrew.length > 0 && (
            <div>
              <div className="overline text-primary/60 mb-3">Preferred therapist (optional)</div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setSelectedCrew("")}
                  className={`px-4 h-10 border rounded-full text-sm ${selectedCrew === "" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                  data-testid="crew-any">Any</button>
                {providingCrew.map((c) => (
                  <button key={c.id} onClick={() => setSelectedCrew(c.id)}
                    className={`px-4 h-10 border rounded-full text-sm ${selectedCrew === c.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                    data-testid={`crew-${c.id}`}>{c.name}</button>
                ))}
              </div>
            </div>
          )}

          {/* Follow-up treatment — same crew, back-to-back, no prep gap.
              Category-first browsing (Feb 2026 refresh): guest picks a
              category tile → treatment thumbnails within that category
              unfurl. Keeps the picker uncluttered when the menu is large. */}
          <div className="border-t border-border pt-8" data-testid="followup-section">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="overline text-primary/60 flex items-center gap-1.5">
                  <ArrowRight className="h-3 w-3" /> Add a follow-up treatment
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Same therapist · no pause between · billed separately
                </div>
              </div>
              {(followup || followupCategory) && (
                <button
                  type="button"
                  onClick={() => { setFollowupId(""); setFollowupCategory(""); }}
                  className="text-xs text-muted-foreground hover:text-destructive"
                  data-testid="followup-clear"
                >
                  Remove
                </button>
              )}
            </div>
            {(() => {
              const isUnderAge = guestIsUnderAge;
              const isAdultOnly = (t) => {
                if (t.adults_only) return true;
                if (t.category_id) {
                  const cat = (allCategories || []).find((c) => c.id === t.category_id);
                  if (cat && cat.adults_only) return true;
                }
                return false;
              };
              // Follow-up candidates = active treatments other than the primary.
              // When the guest picked a specific therapist above, we further
              // narrow to treatments THAT crew can provide. Under-18 guests
              // never see adults-only treatments in the follow-up picker.
              const candidates = (allTreatments || []).filter((t) => {
                if (t.id === treatment.id) return false;
                if (selectedCrew) {
                  const allow = t.crew_ids || [];
                  if (allow.length && !allow.includes(selectedCrew)) return false;
                }
                if (isUnderAge && isAdultOnly(t)) return false;
                return true;
              });
              if (candidates.length === 0) {
                return (
                  <div className="text-xs text-muted-foreground italic" data-testid="followup-none">
                    {selectedCrew ? "This therapist doesn't offer any other treatment." : "No other treatments available."}
                  </div>
                );
              }
              // Group by structured category_id (respects the admin's
              // sort_order + names on `/admin/categories`). Falls back to
              // a synthetic "Other" bucket for treatments without one.
              const byCat = new Map();
              const catOrder = allCategories.map((c) => c.id);
              for (const t of candidates) {
                const key = t.category_id || "__other__";
                if (!byCat.has(key)) byCat.set(key, []);
                byCat.get(key).push(t);
              }
              const categoryList = [];
              for (const cid of catOrder) {
                if (!byCat.has(cid)) continue;
                const cat = allCategories.find((x) => x.id === cid);
                const items = byCat.get(cid);
                categoryList.push({
                  id: cid,
                  name: cat?.name || "Untitled",
                  items,
                  cover: cat?.image_path || (items.find((x) => x.image_path) || items[0])?.image_path || null,
                });
              }
              if (byCat.has("__other__")) {
                const items = byCat.get("__other__");
                categoryList.push({
                  id: "__other__",
                  name: "Other",
                  items,
                  cover: (items.find((x) => x.image_path) || items[0])?.image_path || null,
                });
              }
              const activeGroup = followupCategory
                ? (categoryList.find((c) => c.id === followupCategory)?.items || null)
                : null;

              return (
                <div className="space-y-5">
                  {/* Category tiles — same visual grammar as the main menu */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="followup-categories">
                    {categoryList.map((c) => {
                      const active = followupCategory === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setFollowupCategory(active ? "" : c.id);
                            setFollowupId("");
                          }}
                          className={`relative overflow-hidden aspect-[4/3] border text-left transition-all ${
                            active
                              ? "border-accent ring-2 ring-accent shadow-lg"
                              : "border-border hover:border-primary"
                          }`}
                          data-testid={`followup-category-${c.id}`}
                        >
                          <BlobImage
                            path={c.cover}
                            src={FALLBACK_IMG}
                            alt={c.name}
                            className="absolute inset-0 w-full h-full object-cover opacity-70"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent" />
                          <div className="absolute inset-x-0 bottom-0 p-2.5 text-primary-foreground">
                            <div className="font-medium text-sm truncate">{c.name}</div>
                            <div className="text-[10px] opacity-80">{c.items.length} treatment{c.items.length === 1 ? "" : "s"}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Treatment thumbnails inside the picked category */}
                  {activeGroup && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="followup-options">
                      {activeGroup.map((t) => {
                        const active = followupId === t.id;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setFollowupId(active ? "" : t.id)}
                            className={`relative overflow-hidden aspect-[4/5] border text-left group transition-all ${
                              active
                                ? "border-accent ring-2 ring-accent shadow-lg"
                                : "border-border hover:border-primary"
                            }`}
                            data-testid={`followup-${t.id}`}
                          >
                            <BlobImage
                              path={t.image_path}
                              src={FALLBACK_IMG}
                              alt={t.name}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/50 to-transparent" />
                            {active && (
                              <div className="absolute top-2 right-2 bg-accent text-accent-foreground text-[10px] uppercase tracking-widest px-2 py-0.5">
                                Added
                              </div>
                            )}
                            <div className="absolute inset-x-0 bottom-0 p-3 text-primary-foreground">
                              <div className="font-medium text-sm leading-tight mb-1 line-clamp-2">{t.name}</div>
                              <div className="flex items-center justify-between text-[11px]">
                                <span className="opacity-80 font-mono">+{t.duration_min}min</span>
                                {!t.price_hidden && (
                                  <span className="font-display text-base">€{Number(t.price || 0).toFixed(0)}</span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {followup && (
              <div
                className="mt-5 px-4 py-3 bg-accent/10 border border-accent/40 text-sm"
                data-testid="followup-summary"
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-medium">{treatment.name} → {followup.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Combined {(chosenDuration || treatment.duration_min) + followupDuration} min · back-to-back on the same therapist
                    </div>
                  </div>
                  {!treatment.price_hidden && (
                    <div className="font-display text-lg text-accent whitespace-nowrap">
                      €{(Number(currentPrice) + followupPrice).toFixed(0)}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Optional questions */}
          {(((treatment.questions || []).length + (isFemale ? (treatment.questions_female || []).length : 0)) > 0) && (
            <div className="border-t border-border pt-6">
              <div className="overline text-primary/60 mb-4">A few questions</div>
              <div className="space-y-6" data-testid="questions-list">
                {[...(treatment.questions || []), ...(isFemale ? (treatment.questions_female || []) : [])].map((q) => (
                  <div key={q.id} data-testid={`question-${q.id}`}>
                    <label className="block text-sm font-medium mb-2">{q.text}</label>
                    {q.type === "choice" ? (
                      <div className="flex flex-wrap gap-2">
                        {(q.options || []).map((opt) => (
                          <button
                            key={opt}
                            onClick={() => setAnswers({ ...answers, [q.id]: answers[q.id] === opt ? "" : opt })}
                            className={`px-4 h-10 border rounded-full text-sm transition-colors ${answers[q.id] === opt ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
                            data-testid={`answer-${q.id}-${opt}`}
                          >{opt}</button>
                        ))}
                      </div>
                    ) : (
                      <Input
                        value={answers[q.id] || ""}
                        onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                        placeholder="Your answer…"
                        data-testid={`answer-${q.id}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="overline text-primary/60 mb-3">Notes (optional)</div>
            {/* Email input removed Feb 2026 — email is now captured once
                per trip via the GDPR-consented panel on the shared cabin
                iPad and auto-fed into every booking. Notes stay per-booking. */}
            <Textarea placeholder="Any special requests…" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="border-border resize-none" rows={3} data-testid="guest-notes-input" />
            {savedEmail && (
              <div className="mt-3 text-[11px] text-muted-foreground" data-testid="guest-email-source">
                Confirmation will be sent to <span className="font-mono">{savedEmail}</span> — update on your cabin iPad if needed.
              </div>
            )}
          </div>

          <Button disabled={!slot || submitting || isAdultBlocked} onClick={submit}
            className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-base disabled:opacity-60"
            data-testid="confirm-booking-btn">
            {isAdultBlocked ? (<><Lock className="mr-2 h-4 w-4" /> Adults only (18+)</>) : (<>{submitting ? "Reserving…" : "Confirm Reservation"} <ArrowRight className="ml-2 h-4 w-4" /></>)}
          </Button>
        </div>
      </div>
    </div>
  );
}
